// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import InvoiceSummaryCard, { InvoiceSummaryCardStyles } from "@conklin/App/Components/Common/InvoiceSummaryCard";
import Notify from "@conklin/App/Components/Notify";
import TitleTypography from "@conklin/App/Components/TitleTypography/TitleTypography";
import { xTabEnableZone } from "@conklin/App/Handlers/XNG/Common";
import DataTable from "@conklin/MobiusOverrides/DataTable/DataTable";
import DataTableHeader from "@conklin/MobiusOverrides/DataTable/DataTableHeader";
import Link from "@conklin/MobiusOverrides/Link/Link";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { GetInvoicesApiParameter } from "@insite/client-framework/Services/InvoiceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import loadInvoices from "@insite/client-framework/Store/Data/Invoices/Handlers/LoadInvoices";
import { getInvoicesDataView } from "@insite/client-framework/Store/Data/Invoices/InvoicesSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { CardContainerStyles } from "@insite/content-library/Components/CardContainer";
import { CardListStyles } from "@insite/content-library/Components/CardList";
import { DataTableProps } from "@insite/mobius/DataTable";
import { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import { GridItemProps } from "@insite/mobius/GridItem";
import { DataTableBody, DataTableHead, DataTableRow } from "@insite/mobius/index";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    numberOfRecords = "numberOfRecords",
    payInvoiceLink = "payInvoiceLink",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.numberOfRecords]: number;
        [fields.payInvoiceLink]: string;
    };
}

const recentInvoicesParameter: GetInvoicesApiParameter = {
    customerSequence: "-1",
    page: 1,
    sort: "InvoiceDate DESC",
};

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const session = getSession(state);
    const isAuthenticated = session.isAuthenticated && !session.isGuest;
    recentInvoicesParameter.pageSize = props.fields.numberOfRecords;
    return {
        invoicesDataView: getInvoicesDataView(state, recentInvoicesParameter),
        invoiceHistoryPageLink: getPageLinkByPageType(state, "InvoiceHistoryPage"),
        canViewInvoices: isAuthenticated && (session.userRoles || "").indexOf("Requisitioner") === -1,
        isDashboardPage: getCurrentPage(state).type === "MyAccountPage",
    };
};

const mapDispatchToProps = {
    loadInvoices,
    xTabEnableZone,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecentInvoicesStyles {
    cardList?: CardListStyles;
    errorGridItem?: GridItemProps;
    errorText?: TypographyPresentationProps;
    noInvoicesFoundGridItem?: GridItemProps;
    noInvoicesFoundText?: TypographyPresentationProps;
    cardContainer?: CardContainerStyles;
    invoiceSummaryCard?: InvoiceSummaryCardStyles;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    wrapper?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
}

export const recentInvoicesStyles: RecentInvoicesStyles = {};

const styles = recentInvoicesStyles;

const RecentInvoices: React.FC<Props> = ({
    fields,
    canViewInvoices,
    invoicesDataView,
    loadInvoices,
    invoiceHistoryPageLink,
    isDashboardPage,
    xTabEnableZone,
    zone,
}) => {
    useEffect(() => {
        if (canViewInvoices && !invoicesDataView.value && !invoicesDataView.isLoading) {
            loadInvoices(recentInvoicesParameter);
        }
    }, [canViewInvoices, invoicesDataView]);

    xTabEnableZone({ zone, isEnabled: true });

    if (!canViewInvoices || !invoicesDataView.value) {
        return null;
    }

    const invoiceHistoryUrl = invoiceHistoryPageLink ? invoiceHistoryPageLink.url : undefined;

    return (
        <StyledWrapper {...styles.wrapper} data-test-selector="widget_RecentInvoices">
            {isDashboardPage && (
                <TitleTypography linkText={translate("View All Invoices")} link={invoiceHistoryUrl} showIcon={true}>
                    {translate("Recent Invoices")}
                </TitleTypography>
            )}
            {invoicesDataView.value.length === 0 && (
                <Notify
                    type="warning"
                    fullWidth={true}
                    showClose={false}
                    data-test-selector="recentInvoices_NoInvoicesFound"
                >
                    {siteMessage("Custom_No_Recent_Invoices_Message")}
                </Notify>
            )}
            {invoicesDataView.value.length > 0 && (
                <>
                    <DataTable {...styles.dataTable}>
                        <DataTableHead {...styles.dataTableHead}>
                            <DataTableHeader {...styles.dataTableHeaders}>{translate("Invoice #")}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{translate("PO #")}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{translate("Invoice Date")}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{translate("Due Date")}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{translate("Total")}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{translate("Balance")}</DataTableHeader>
                            <DataTableHeader {...styles.dataTableHeaders}>{translate("Pay Invoice")}</DataTableHeader>
                        </DataTableHead>
                        <DataTableBody {...styles.dataTableBody}>
                            {invoicesDataView.value.map(
                                invoice =>
                                    invoice !== null && (
                                        <DataTableRow
                                            className="product-row"
                                            data-test-selector="recentInvoices_dataLine"
                                            key={invoice.id}
                                        >
                                            <InvoiceSummaryCard
                                                invoice={invoice}
                                                payInvoiceLink={fields.payInvoiceLink}
                                                extendedStyles={styles.invoiceSummaryCard}
                                            />
                                        </DataTableRow>
                                    ),
                            )}
                        </DataTableBody>
                    </DataTable>
                    {!isDashboardPage && (
                        <Link href={invoiceHistoryUrl} {...styles.viewAllLink}>
                            {translate("View All Invoices")}
                        </Link>
                    )}
                </>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecentInvoices),
    definition: {
        group: "Common",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.numberOfRecords,
                displayName: "Number of Recent Invoices Displayed",
                editorTemplate: "IntegerField",
                min: 1,
                defaultValue: 5,
                fieldType: "General",
            },
            {
                name: "payInvoiceLink",
                fieldType: "General",
                editorTemplate: "TextField",
                defaultValue: "",
            },
        ],
    },
};

export default widgetModule;
